import { useMemo } from "react";
import { Image, Input, View } from "@/components/ui";
import { getCountryFlagUrl, type CountryCode as CountryCodeHelper } from "@/lib/country-flags";
import { ensurePhoneNumberWithPlus } from "@/lib/helpers/string";
import { cn } from "@/lib/utils";
import { AsYouType, parsePhoneNumberWithError, type CountryCode } from "libphonenumber-js";

const getCountryCodeFromPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber || phoneNumber === "+") return "sg";
  try {
    const { country } = parsePhoneNumberWithError(phoneNumber) || {};
    return country?.toLowerCase() || "sg";
  } catch {
    return "sg";
  }
};

export const InputPhoneNumberContent = ({
  value,
  onChange,
  inputClassName,
  error,
  disabled,
}: {
  value: string | undefined;
  onChange?: (value: string) => void;
  inputClassName?: string;
  error?: string;
  disabled?: boolean;
}) => {
  const handleChange = (text: string) => {
    if (!onChange) return;
    const formatted = ensurePhoneNumberWithPlus(text);
    onChange(formatted);
  };

  const countryCode = getCountryCodeFromPhoneNumber(value || "");

  const previewValue = useMemo(() => {
    if (!value || value === "+") return value || "";
    try {
      const formatter = new AsYouType(countryCode.toUpperCase() as CountryCode);
      return formatter.input(value);
    } catch {
      return value;
    }
  }, [value, countryCode]);

  return (
    <View
      className={cn(
        "flex h-12 flex-row gap-0 overflow-hidden rounded-md border",
        error ? "border-danger" : "border-border",
      )}
    >
      <View className="border-border flex items-center justify-center border-r px-3">
        <Image src={getCountryFlagUrl(countryCode as CountryCodeHelper)} width={24} height={17} />
      </View>
      <Input
        value={previewValue}
        onChangeText={handleChange}
        className={cn(
          inputClassName,
          "placeholder:text-foreground-intermediate h-full w-full flex-1 border-0 px-6",
        )}
        placeholder="e.g. +6512345678"
        inputMode="numeric"
        type="tel"
        disabled={disabled}
      />
    </View>
  );
};
